// https://echarts.apache.org/en/option.html
import moment from 'moment';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import uniq from 'lodash/uniq';

import { __t, applyTranslations } from 'utils/language';
import { abbrNum } from 'utils/helper/common';
import { COLORS } from 'utils/reporting';

import type {
  BarGraphMultiDataType,
  BarGraphMultiDataZoom,
  BarGraphMultiGrid,
  BarGraphMultiKpi,
  BarGraphMultiLegend,
  BarGraphMultiTitle,
  BarGraphMultiXAxisOptions,
  BarGraphMultiYAxisOptions
} from '../types';
import type {
  ContentPerformanceDataType,
  ContentPerformanceKpi
} from 'modules/reporting/components/content-performance/types';

type StringNumber = string | number;

applyTranslations(require('locales/common/collabary-common.i18n.json'));

const COMMON_MSG_KEY = 'collabary_common';

export const getFormattedDate = (value: StringNumber): string => (
  moment(value).format('ddd Do')
);

export const getDisplayDateRange = (
  dateRange: Array<string>
): string => {
  if (!isArray(dateRange) || isEmpty(dateRange)) {
    return '';
  }

  if (dateRange.length === 1) {
    return dateRange[0];
  }

  return `${dateRange[0]} - ${dateRange[dateRange.length - 1]}`;
};

export const getColorOptions = (
  dataTypes: Array<BarGraphMultiDataType | ContentPerformanceDataType>
): Array<string> => {
  const colors = dataTypes.map(dataType => COLORS[String(dataType)].main);

  return uniq(colors);
};

export const getGridOptions = (): BarGraphMultiGrid => ({
  width: '92%',
  right: '2%',
  bottom: 130,
  top: 20,
  left: '7%'
});

export const getTitleOptions = (
  dateRange: Array<string>
): BarGraphMultiTitle => ({
  text: getDisplayDateRange(dateRange),
  bottom: 60,
  left: 'center',
  itemGap: 0,
  textStyle: {
    fontFamily: 'Hind',
    fontSize: 12,
    fontWeight: 600,
    color: '#8d8d8d'
  }
});

export const getDataZoomOptions = (): Array<BarGraphMultiDataZoom> => [{
  type: 'inside',
  xAxisIndex: [0],
  start: 0,
  end: 100
}];

export const getLegendOptions = (): BarGraphMultiLegend => ({
  type: 'plain',
  orient: 'horizontal',
  width: '75%',
  bottom: 25,
  icon: 'circle',
  itemWidth: 8,
  itemHeight: 8,
  itemGap: 40,
  inactiveColor: '#c5c5c5',
  textStyle: {
    fontFamily: 'Hind',
    fontSize: 12,
    padding: [3, 0, 0, 4]
  },
  formatter: (name: string): string => (
    __t(`${COMMON_MSG_KEY}.types.${name}.plural`)
  )
});

export const getXAxisOptions = (): BarGraphMultiXAxisOptions => ({
  type: 'category',
  axisTick: {
    alignWithLabel: true,
    length: 9
  },
  axisLine: {
    onZero: false,
    lineStyle: {
      color: '#e0e0e0'
    }
  },
  axisLabel: {
    color: '#3a3a3a',
    margin: 13,
    formatter: (value: StringNumber): string => (
      getFormattedDate(value)
    )
  }
});

export const getYAxisOptions = (
  kpi: BarGraphMultiKpi | ContentPerformanceKpi
): BarGraphMultiYAxisOptions => ({
  type: 'value',
  max: 'dataMax',
  splitNumber: 10,
  nameGap: 10,
  nameTextStyle: {
    color: '#3a3a3a'
  },
  axisTick: {
    alignWithLabel: true,
    length: 9
  },
  axisLine: {
    lineStyle: {
      color: '#e0e0e0'
    }
  },
  axisLabel: {
    formatter: (value: number): string => (
      kpi === 'engagementRateFollowers' ?
        `${value.toFixed(2)}%`
        : abbrNum(value, {}, true)
    ),
    color: '#3a3a3a',
    margin: 13,
    showMinLabel: false,
    showMaxLabel: false
  },
  splitLine: {
    type: 'value',
    show: true,
    lineStyle: {
      color: '#ebebeb',
      type: 'dashed'
    }
  }
});
